// --- GWP (free gift) eligibility constants ---
const GWP_TRIGGER_HANDLES = [
  "easy-bake-loose-baking-setting-powder-hb00140m",
  "easy-bake-fragrance-free-loose-baking-and-setting-powder-hb01260m",
]; // product handles eligible for the gift
const GWP_HANDLE = "easy-bake-sifter-without-mesh-hb01571"; // gift handle

const VANITY_BAG_HANDLE = "mini-vanity-bag-hb01764"; // vanity-bag gift handle
const VANITY_BAG_MIN_SUBTOTAL = {
  USD: 20000,
  AED: 73450,
  AUD: 28139,
  CAD: 27181,
  EUR: 16850,
  GBP: 14660,
  QAR: 72800,
  SAR: 75000,
};

const GIFT_DRAWER_ARM_MS = 1000;
const GIFT_DRAWER_HOLD_MS = 5000;
const GIFT_DRAWER_POLL_MS = 100;
const POST_DK_GRACE_MS = 3000;
const HB_GIFT_PROPERTY_KEY = "_hb_gift";
const HB_CART_REFRESH_QUERY_PARAM = "hb_cart_refresh";

function getGiftPickerCampaignThresholdCents(campaign, currency) {
  const thresholdsCents = campaign?.thresholdsCents || {};
  const thresholdCents = Number(thresholdsCents[currency] ?? thresholdsCents.USD);

  if (!Number.isFinite(thresholdCents) || thresholdCents <= 0) {
    return 0;
  }

  return thresholdCents;
}

function getGiftPickerCampaigns(cartState) {
  const config = window.hbGiftPickerConfig || {};
  if (config.enabled !== true) return [];

  const currency = String(cartState?.currency || "USD").toUpperCase();

  if (Array.isArray(config.campaigns) && config.campaigns.length) {
    return config.campaigns
      .map((campaign) => {
        const propertyValue = String(campaign?.propertyValue || "").trim();
        const thresholdCents = getGiftPickerCampaignThresholdCents(campaign, currency);
        const triggerProductIds = Array.isArray(campaign?.triggerProductIds)
          ? campaign.triggerProductIds.map((id) => Number(id)).filter((id) => Number.isFinite(id) && id > 0)
          : [];
        const triggerBundleIds = Array.isArray(campaign?.triggerBundleIds)
          ? campaign.triggerBundleIds.map((id) => Number(id)).filter((id) => Number.isFinite(id) && id > 0)
          : [];
        const ignorePriceThresholdWhenTriggered = campaign?.ignorePriceThresholdWhenTriggered === true;
        const requireAllTriggerProductsInCart = campaign?.requireAllTriggerProductsInCart === true;
        const hasTriggerRequirement = triggerProductIds.length > 0;

        if (!propertyValue) {
          return null;
        }

        if (thresholdCents <= 0 && !(ignorePriceThresholdWhenTriggered && hasTriggerRequirement)) {
          return null;
        }

        return {
          id: String(campaign?.id || propertyValue),
          propertyValue,
          thresholdCents,
          triggerProductIds,
          triggerBundleIds,
          ignorePriceThresholdWhenTriggered,
          requireAllTriggerProductsInCart,
        };
      })
      .filter(Boolean);
  }

  const thresholdsCents = config.thresholdsCents || {};
  const thresholdCents = Number(thresholdsCents[currency] ?? thresholdsCents.USD);

  if (Number.isFinite(thresholdCents) && thresholdCents > 0) {
    return [
      {
        id: "legacy",
        propertyValue: String(config.legacyPropertyValue).trim(),
        thresholdCents,
        triggerProductIds: [],
      },
    ];
  }

  return [];
}

function getGiftPickerValidationRules() {
  const config = window.hbGiftPickerConfig || {};
  const validationRules = config.validationRules || {};
  
  const excludedProductIds = Array.isArray(validationRules.excludedProductIds)
    ? validationRules.excludedProductIds
        .map((id) => Number(id))
        .filter((id) => Number.isFinite(id) && id > 0)
    : [];

  const compareAtVariantIds = Array.isArray(validationRules.compareAtVariantIds)
    ? validationRules.compareAtVariantIds
        .map((id) => Number(id))
        .filter((id) => Number.isFinite(id) && id > 0)
    : [];

  return {
    excludedProductIds,
    excludedProductIdsSet: new Set(excludedProductIds),
    excludeCompareAtPriceItems: validationRules.excludeCompareAtPriceItems === true,
    compareAtVariantIds,
    compareAtVariantIdsSet: new Set(compareAtVariantIds),
  };
}

function isHbGiftItem(item) {
  const value = item?.properties?.[HB_GIFT_PROPERTY_KEY];
  return value !== undefined && value !== null && String(value).trim() !== "";
}

function getGiftPickerEligibilitySubtotalCents(cartState, validationRules = null) {
  const resolvedValidationRules = validationRules || getGiftPickerValidationRules();

  const hasCollectionRule = resolvedValidationRules.excludedProductIdsSet.size > 0;
  const hasCompareAtRule =
    resolvedValidationRules.excludeCompareAtPriceItems === true &&
    resolvedValidationRules.compareAtVariantIdsSet.size > 0;

  const shouldApplyValidationRules = hasCollectionRule || hasCompareAtRule;

  let giftEligibilitySubtotalCents = 0;
  const totalPrice = Number(cartState?.total_price);
  if (Number.isFinite(totalPrice) && totalPrice >= 0) {
    giftEligibilitySubtotalCents = totalPrice;
  }

  if (!giftEligibilitySubtotalCents) {
    const normalSubtotal = Number(cartState?.items_subtotal_price ?? cartState?.original_total_price ?? 0);
    if (Number.isFinite(normalSubtotal) && normalSubtotal >= 0) {
      giftEligibilitySubtotalCents = normalSubtotal;
    }
  }

  if (!shouldApplyValidationRules) {
    return giftEligibilitySubtotalCents;
  }

  const cartItems = Array.isArray(cartState?.items) ? cartState.items : [];
  let excludedTotalCents = 0;

  cartItems.forEach((item) => {
    const productId = Number(item?.product_id);
    const variantId = Number(item?.variant_id);

    let itemExcluded = false;

    if (
      Number.isFinite(productId) &&
      productId > 0 &&
      resolvedValidationRules.excludedProductIdsSet.has(productId)
    ) {
      itemExcluded = true;
    }

    if (
      resolvedValidationRules.excludeCompareAtPriceItems === true &&
      Number.isFinite(variantId) &&
      variantId > 0 &&
      resolvedValidationRules.compareAtVariantIdsSet.has(variantId)
    ) {
      itemExcluded = true;
    }

    if (!itemExcluded) return;

    const itemFinalLinePrice = Number(item?.final_line_price ?? item?.line_price ?? 0);
    if (Number.isFinite(itemFinalLinePrice) && itemFinalLinePrice > 0) {
      excludedTotalCents += itemFinalLinePrice;
    }
  });

  return Math.max(giftEligibilitySubtotalCents - excludedTotalCents, 0);
}

function isGiftPickerCampaignEligible(campaign, cartSubtotalCents, cartProductIdsInCart) {
  if (!campaign) {
    return false;
  }

  const triggerProductIds = Array.isArray(campaign.triggerProductIds) ? campaign.triggerProductIds : [];
  const triggerBundleIds = Array.isArray(campaign.triggerBundleIds) ? campaign.triggerBundleIds : [];
  const hasTriggerRequirement = triggerProductIds.length > 0;

  if (hasTriggerRequirement) {
    const requireAllTriggerProductsInCart = campaign.requireAllTriggerProductsInCart === true;
    let triggersSatisfied;
    if (requireAllTriggerProductsInCart) {
      const allProductsInCart = triggerProductIds.every((productId) => cartProductIdsInCart.has(productId));
      const bundleSatisfied = triggerBundleIds.length > 0 && triggerBundleIds.some((productId) => cartProductIdsInCart.has(productId));
      triggersSatisfied = allProductsInCart || bundleSatisfied;
    } else {
      triggersSatisfied = triggerProductIds.some((productId) => cartProductIdsInCart.has(productId));
    }
    if (!triggersSatisfied) {
      return false;
    }

    if (campaign.ignorePriceThresholdWhenTriggered === true) {
      return true;
    }
  }

  return cartSubtotalCents >= campaign.thresholdCents;
}

async function removeIneligibleGiftsWithSingleRequest(cartState, sectionsToRender = []) {
  try {
    const currentItems = Array.isArray(cartState?.items) ? cartState.items : [];
    if (!currentItems.length) return cartState;

    const updates = {};
    const hasGwpTriggerInCart = currentItems.some((item) => GWP_TRIGGER_HANDLES.includes(item.handle));

    if (!hasGwpTriggerInCart) {
      currentItems.forEach((item) => {
        if (item.handle === GWP_HANDLE && item.key) {
          updates[item.key] = 0;
        }
      });
    }

    const giftPickerCampaigns = getGiftPickerCampaigns(cartState);
    if (giftPickerCampaigns.length) {
      const validationRules = getGiftPickerValidationRules();
      const cartSubtotalCents = getGiftPickerEligibilitySubtotalCents(cartState, validationRules);
      const cartProductIdsInCart = new Set(
        currentItems.map((item) => Number(item?.product_id)).filter((productId) => Number.isFinite(productId) && productId > 0),
      );
      const campaignByPropertyValue = new Map(
        giftPickerCampaigns.map((campaign) => [campaign.propertyValue, campaign]),
      );

      currentItems.forEach((item) => {
        if (!isHbGiftItem(item) || !item.key) return;

        const giftPropertyValue = String(item?.properties?.[HB_GIFT_PROPERTY_KEY] || "").trim();
        const campaign = campaignByPropertyValue.get(giftPropertyValue);

        if (campaign) {
          if (!isGiftPickerCampaignEligible(campaign, cartSubtotalCents, cartProductIdsInCart)) {
            updates[item.key] = 0;
          }
          return;
        }

        if (giftPropertyValue !== "") {
          updates[item.key] = 0;
        }
      });
    } else {
      currentItems.forEach((item) => {
        if (!isHbGiftItem(item) || !item.key) return;
        updates[item.key] = 0;
      });
    }

    if (!Object.keys(updates).length) return cartState;

    const payload = { updates };
    if (Array.isArray(sectionsToRender) && sectionsToRender.length) {
      payload.sections = sectionsToRender;
      payload.sections_url = window.location.pathname;
    }

    const response = await fetch(`${routes.cart_update_url}`, {
      ...fetchConfig(),
      body: JSON.stringify(payload),
    });
    const responseText = await response.text();
    const updatedState = responseText ? JSON.parse(responseText) : cartState;

    sessionStorage.removeItem("dk_auto_added_gifts");
    return updatedState;
  } catch (err) {
    console.error("GWP auto-removal failed:", err);
    return cartState;
  }
}
window.hbRemoveIneligibleGifts = window.hbRemoveIneligibleGifts || removeIneligibleGiftsWithSingleRequest;

function sleep(ms) {
  return new Promise((r) => setTimeout(r, ms));
}

async function waitForGiftCartIfBusy() {
  const armDeadline = Date.now() + GIFT_DRAWER_ARM_MS;
  while (sessionStorage.getItem("dk_gwp_updating") === null && Date.now() < armDeadline) {
    await sleep(GIFT_DRAWER_POLL_MS);
  }

  if (sessionStorage.getItem("dk_gwp_updating") !== "1") return;

  const holdDeadline = Date.now() + GIFT_DRAWER_HOLD_MS;
  while (sessionStorage.getItem("dk_gwp_updating") === "1" && Date.now() < holdDeadline) {
    await sleep(GIFT_DRAWER_POLL_MS);
  }

  await sleep(POST_DK_GRACE_MS);
}
window.waitForGiftCartIfBusy = window.waitForGiftCartIfBusy || waitForGiftCartIfBusy;

const calcHazmatVolumeFromHtml = (html) => {
  setHazmatDismissed(false);

  const doc = new DOMParser().parseFromString(html, "text/html");
  let total = 0;
  const scope = doc.getElementById("main-cart-items") || doc;

  scope.querySelectorAll("[data-hazmat-flag]").forEach((node) => {
    const flag = node.dataset.hazmatFlag;
    if (flag !== "1" && flag !== "true") return;

    const vol = parseFloat(node.dataset.hazmatVol || "");
    if (Number.isNaN(vol)) return;

    const qty = parseInt(node.querySelector(".quantity__input")?.value || "1", 10) || 1;
    total += vol * qty;
  });

  return total;
};

function saveHazmatVolume(vol) {
  fetch(routes.cart_update_url, {
    ...fetchConfig(),
    body: JSON.stringify({
      attributes: { _prevHazmatVolume: String(vol) },
    }),
  });
}

class CartRemoveButton extends HTMLElement {
  constructor() {
    super();

    this.addEventListener("click", (event) => {
      event.preventDefault();
      const cartItems = this.closest("cart-items") || this.closest("cart-drawer-items");
      const lineIndex = this.dataset.index;
      const quantityInput = document.querySelector(`#Quantity-${lineIndex}, #Drawer-quantity-${lineIndex}`);
      const variantId = quantityInput?.dataset?.quantityVariantId || null;

      cartItems.updateQuantity(this.dataset.index, 0, "updates[]", variantId);
    });
  }
}

customElements.define("cart-remove-button", CartRemoveButton);

class CartItems extends HTMLElement {
  constructor() {
    super();
    this.lineItemStatusElement =
      document.getElementById("shopping-cart-line-item-status") || document.getElementById("CartDrawer-LineItemStatus");
    this.cartDrawer = document.getElementById("CartDrawer");
    this.mainCartItems = document.getElementById("main-cart-items");
    this.isUploading = false;

    const debouncedOnChange = (event) => {
      const target = event.target;

      if (target.tagName === "INPUT" && target.type === "number" && target.classList.contains("quantity__input")) {
        this.onChange(event);
      }
    };

    this.addEventListener("change", debouncedOnChange.bind(this));
  }

  cartUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      if (event.source === "cart-items" || event.source === "product-form" || event.source === "product-form-post-silent") {
        return;
      }
      this.onCartUpdate(event?.cartData || null);
    });

    const cartWrapper = document.querySelector("cart-drawer") || this.closest(".cart-page");
    const isCartEmpty = cartWrapper?.classList.contains("is-empty") || this.classList.contains("is-empty");
    if (isCartEmpty) {
      sessionStorage.removeItem("dk_auto_added_gifts");
    }

    if (this.tagName === "CART-ITEMS" && !window.__hbCartPageGiftCleanupRan) {
      window.__hbCartPageGiftCleanupRan = true;
      this.toggleOverlay(true);
      this.runInitialCartPageGiftCleanup();
    }

    if (this.tagName === "CART-ITEMS") {
      this._onPageShow = async (event) => {
        const shouldForceRefresh = this.shouldForceCartRefreshFromUrl();
        if (!event?.persisted && !shouldForceRefresh) return;
        if (shouldForceRefresh) {
          this.clearForcedCartRefreshFromUrl();
        }
        this.toggleOverlay(true);
        this.runInitialCartPageGiftCleanup();
        try {
          const giftCleanupUpdatedCart = await window.__hbCartGiftCleanupPromise;
          if (!giftCleanupUpdatedCart) await this.updateCartPage();
        } finally {
          this.toggleOverlay(false);
        }
      };
      window.addEventListener("pageshow", this._onPageShow);

      let isBackForward = false;
      if (!window.__hbCartBackForwardRefreshDone) {
        try {
          const nav = performance.getEntriesByType("navigation")[0];
          isBackForward = nav?.type === "back_forward";
        } catch {}
      }

      if (isBackForward) {
        window.__hbCartBackForwardRefreshDone = true;
        const ready = window.__hbCartGiftCleanupPromise || Promise.resolve(false);
        ready.then(async (giftCleanupUpdatedCart) => {
          try {
            if (!giftCleanupUpdatedCart) await this.updateCartPage();
          } finally {
            this.toggleOverlay(false);
          }
        });
      } else {
        const ready = window.__hbCartGiftCleanupPromise;
        if (ready) {
          ready.then(() => this.toggleOverlay(false));
        }
      }
    }
  }

  disconnectedCallback() {
    if (this.cartUpdateUnsubscriber) {
      this.cartUpdateUnsubscriber();
    }
    if (this._onPageShow) {
      window.removeEventListener("pageshow", this._onPageShow);
      this._onPageShow = null;
    }
  }

  async removeGiftsIfIneligible(cartState) {
    const sectionsToRender = this.getSectionsToRender()
      .map((section) => section.section)
      .filter(Boolean);
    return await removeIneligibleGiftsWithSingleRequest(cartState, sectionsToRender);
  }

  shouldForceCartRefreshFromUrl() {
    try {
      return new URL(window.location.href).searchParams.get(HB_CART_REFRESH_QUERY_PARAM) === "1";
    } catch {
      return false;
    }
  }

  clearForcedCartRefreshFromUrl() {
    try {
      const url = new URL(window.location.href);
      if (!url.searchParams.has(HB_CART_REFRESH_QUERY_PARAM)) return;

      url.searchParams.delete(HB_CART_REFRESH_QUERY_PARAM);
      const nextUrl = `${url.pathname}${url.search}${url.hash}`;
      window.history.replaceState(window.history.state, "", nextUrl);
    } catch {}
  }

  runInitialCartPageGiftCleanup() {
    let resolveReady;
    window.__hbCartGiftCleanupPromise = new Promise((r) => { resolveReady = r; });

    const run = async () => {
      let didUpdateCart = false;
      try {
        const config = window.hbGiftPickerConfig;
        if (!config || typeof config.enabled !== "boolean") return;
        if (typeof window.hbRemoveIneligibleGifts !== "function") return;

        const cartState = await this.fetchCart();
        if (!cartState || !Array.isArray(cartState.items) || !cartState.items.length) return;

        const sectionsToRender = this.getSectionsToRender()
          .map((section) => section.section)
          .filter(Boolean);

        const cleanedState = await window.hbRemoveIneligibleGifts(cartState, sectionsToRender);

        if (!cleanedState || cleanedState === cartState) return;

        didUpdateCart = true;
        const becameEmpty = Number(cleanedState.item_count) === 0;

        if (becameEmpty) {
          sessionStorage.removeItem("dk_auto_added_gifts");
          await this.updateCartPage();
        } else if (cleanedState.sections) {
          this.renderFromSections(cleanedState.sections);
        }

        if (typeof publish === "function" && PUB_SUB_EVENTS?.cartUpdate) {
          publish(PUB_SUB_EVENTS.cartUpdate, {
            source: "cart-items",
            cartData: cleanedState,
          });
        }
      } catch (err) {
        console.error("Cart-page initial gift cleanup failed:", err);
      } finally {
        resolveReady(didUpdateCart);
      }
    };

    if (document.readyState === "loading") {
      document.addEventListener("DOMContentLoaded", run, { once: true });
    } else {
      setTimeout(run, 0);
    }
  }

  resetQuantityInput(id) {
    const input = this.querySelector(`#Quantity-${id}`);
    input.value = input.getAttribute("value");
    this.isEnterPressed = false;
  }

  setValidity(event, index, message) {
    event.target.setCustomValidity(message);
    event.target.reportValidity();
    this.resetQuantityInput(index);
    event.target.select();
  }

  validateQuantity(event) {
    const inputValue = parseInt(event.target.value);
    const index = event.target.dataset.index;
    const activeElementName = document.activeElement?.getAttribute?.("name") || event.target.getAttribute("name");
    let message = "";

    if (inputValue < event.target.dataset.min) {
      message = window.quickOrderListStrings.min_error.replace("[min]", event.target.dataset.min);
    } else if (inputValue > parseInt(event.target.max)) {
      message = window.quickOrderListStrings.max_error.replace("[max]", event.target.max);
    } else if (inputValue % parseInt(event.target.step) !== 0) {
      message = window.quickOrderListStrings.step_error.replace("[step]", event.target.step);
    }

    if (message) {
      this.setValidity(event, index, message);
    } else {
      event.target.setCustomValidity("");
      event.target.reportValidity();
      this.updateQuantity(
        index,
        inputValue,
        activeElementName,
        event.target.dataset.quantityVariantId,
      );
    }
  }

  fetchCart = async () => {
    return await fetch("/cart.js")
      .then((res) => res.json())
      .catch((err) => console.error("Error fetching cart:", err));
  };

  onChange(event) {
    this.validateQuantity(event);
  }

  async onCartUpdate(publishedCartState = null) {
    if (this.tagName === "CART-DRAWER-ITEMS") {
      this.toggleOverlay(true);
      const sectionsToFetch = ["cart-drawer", "cart-icon-bubble"];
      const sectionsParam = sectionsToFetch.join(",");
      let sectionsRequest = null;

      try {
        let workingCartState = publishedCartState;
        if (!workingCartState && typeof this.fetchCart === "function") {
          workingCartState = await this.fetchCart();
        }

        if (workingCartState) {
          const cleanedCartState = await this.removeGiftsIfIneligible(workingCartState);
          if (cleanedCartState?.sections) {
            sectionsRequest = Promise.resolve(cleanedCartState.sections);
          }
        }
      } catch (error) {
        console.error("Gift cleanup before cart drawer refresh failed:", error);
      }

      if (!sectionsRequest) {
        sectionsRequest = fetch(`${routes.cart_url}?sections=${sectionsParam}`).then((response) => response.json());
      }

      sectionsRequest
        .then((sectionsData) => {
          const cartDrawerHtml = sectionsData["cart-drawer"];
          if (cartDrawerHtml) {
            const html = new DOMParser().parseFromString(cartDrawerHtml, "text/html");
            const selectors = ["cart-drawer-items", ".cart-drawer__footer", ".drawer__header", ".free-shipping-box"];
            for (const selector of selectors) {
              const targetElement = document.querySelector(selector);
              const sourceElement = html.querySelector(selector);
              if (targetElement && sourceElement) {
                targetElement.replaceWith(sourceElement);
              }
            }

            const cartDrawer = document.querySelector("cart-drawer");
            const sourceCartDrawer = html.querySelector("cart-drawer");
            const isCartEmpty = sourceCartDrawer?.classList.contains("is-empty");

            if (cartDrawer) {
              if (isCartEmpty) {
                cartDrawer.classList.add("is-empty");
                sessionStorage.removeItem("dk_auto_added_gifts");
              } else {
                cartDrawer.classList.remove("is-empty");
              }
            }

            const currentEmptyContent = document.querySelector(".drawer__inner-empty");
            const sourceEmptyContent = html.querySelector(".drawer__inner-empty");
            const drawerInner = document.querySelector(".drawer__inner");

            if (isCartEmpty && sourceEmptyContent && !currentEmptyContent && drawerInner) {
              const freeShippingBox = drawerInner.querySelector(".free-shipping-box");
              if (freeShippingBox) {
                freeShippingBox.insertAdjacentElement("afterend", sourceEmptyContent.cloneNode(true));
              }
            } else if (!isCartEmpty && currentEmptyContent) {
              currentEmptyContent.remove();
            }

            try {
              const newHazmatVolume = calcHazmatVolumeFromHtml(cartDrawerHtml);
              saveHazmatVolume(newHazmatVolume);
            } catch (err) {
              console.error("Unable to calculate/save hazmat volume", err);
            }
          }

          const cartIconHtml = sectionsData["cart-icon-bubble"];
          if (cartIconHtml) {
            const iconHtmlDoc = new DOMParser().parseFromString(cartIconHtml, "text/html");
            const targetIcon = document.getElementById("cart-icon-bubble");
            const sourceIcon = iconHtmlDoc.querySelector(".shopify-section");
            if (targetIcon && sourceIcon) {
              targetIcon.innerHTML = sourceIcon.innerHTML;
            }
          }
        })
        .catch((e) => {
          console.error(e);
        })
        .finally(() => {
          this.releaseOverlayAfterGifts();
        });
    } else {
      this.toggleOverlay(true);
      try {
        await this.updateCartPage();
      } finally {
        this.releaseOverlayAfterGifts();
      }
    }
  }

  getSectionsToRender() {
    const sections = [
      {
        id: "cart-page-milestone-progress",
        section: document.getElementById("cart-page-milestone-progress")?.dataset.id,
        selector: ".progress-section",
      },
      {
        id: "cart-samples-gifts-tabs",
        section: document.getElementById("cart-samples-gifts-tabs")?.dataset.id,
        selector: ".js-contents",
      },
      {
        id: "main-cart-items",
        section: document.getElementById("main-cart-items").dataset.id,
        selector: ".js-contents",
      },
      {
        id: "cart-live-region-text",
        section: "cart-live-region-text",
        selector: ".shopify-section",
      },
      {
        id: "main-cart-footer",
        section: document.getElementById("main-cart-footer").dataset.id,
        selector: ".cart__footer",
      },
    ];

    return sections.filter((section) => section.section);
  }

  updateCartPage = () => {
    if (window.location.pathname.includes("/cart")) {
      return fetch(`${routes.cart_url}`)
        .then((response) => response.text())
        .then((responseText) => {
          const html = new DOMParser().parseFromString(responseText, "text/html");

          const selectors = [".content-for-layout"];
          for (const selector of selectors) {
            const targetElement = document.querySelector(selector);
            const sourceElement = html.querySelector(selector);
            if (targetElement && sourceElement) {
              targetElement.replaceWith(sourceElement);
            }
          }
          if (typeof window.initializeCartCheckoutButtons === "function") {
            window.initializeCartCheckoutButtons();
          }
          try {
            const newHazmatVolume = calcHazmatVolumeFromHtml(responseText);
            saveHazmatVolume(newHazmatVolume);
          } catch (err) {
            console.error("Unable to calculate/save hazmat volume", err);
          }
        })
        .catch((e) => {
          console.error(e);
        });
    }
    return Promise.resolve();
  };

  renderFromSections = (sectionsMap) => {
    this.getSectionsToRender().forEach((section) => {
      const container = document.getElementById(section.id);
      if (!container) return;

      const elementToReplace = container.querySelector(section.selector) || container;
      const html = sectionsMap[section.section];
      if (!html) return;
      const preservedScrollState = this.getPreservedSectionScrollState(section.id);

      elementToReplace.innerHTML = this.getSectionInnerHTML(html, section.selector);
      this.restorePreservedSectionScrollState(section.id, preservedScrollState);

      if (section.id === "CartDrawer" || section.id === "main-cart-items") {
        try {
          const newHazmatTotal = calcHazmatVolumeFromHtml(html);
          saveHazmatVolume(newHazmatTotal);
        } catch (err) {
          console.error("Unable to calc hazmat volume", err);
        }
      }
    });
  };

  getPreservedSectionScrollState(sectionId) {
    if (sectionId !== "main-cart-items") return null;

    const scrollContainer = document.querySelector("#main-cart-items .cart-items--list");
    if (!scrollContainer) return null;

    return {
      selector: ".cart-items--list",
      top: scrollContainer.scrollTop,
      left: scrollContainer.scrollLeft,
    };
  }

  restorePreservedSectionScrollState(sectionId, scrollState) {
    if (sectionId !== "main-cart-items" || !scrollState) return;

    requestAnimationFrame(() => {
      const sectionRoot = document.getElementById(sectionId);
      const scrollContainer = sectionRoot?.querySelector(scrollState.selector);
      if (!scrollContainer) return;

      const maxScrollTop = Math.max(0, scrollContainer.scrollHeight - scrollContainer.clientHeight);
      scrollContainer.scrollTop = Math.min(scrollState.top, maxScrollTop);
      scrollContainer.scrollLeft = scrollState.left;
    });
  }

  fetchSectionsJson = async () => {
    const sections = this.getSectionsToRender()
      .map((s) => s.section)
      .join(",");
    const url = `${routes.cart_url}?sections=${encodeURIComponent(sections)}`;
    const res = await fetch(url, { credentials: "same-origin" });
    if (!res.ok) throw new Error("Failed to fetch sections");
    return await res.json();
  };

  async updateQuantity(line, quantity, name, variantId) {
    if (this.isUploading) return;
    this.isUploading = true;
    const isCartDrawerContext = this.tagName === "CART-DRAWER-ITEMS";
    const shouldClearCartActionFocus = ["updates[]", "plus", "minus"].includes(name);
    const shouldPreservePageScroll = !isCartDrawerContext && shouldClearCartActionFocus;
    const preservedScrollPosition = shouldPreservePageScroll
      ? { left: window.scrollX, top: window.scrollY }
      : null;
    if (quantity === 0) {
      DLRemoveFromCart(line);
    }

    try {
    await window.__cartQueue.enqueue(async () => {
    this.enableLoading(line);

    if (shouldClearCartActionFocus && typeof document.activeElement?.blur === "function") {
      document.activeElement.blur();
    }

    const offlineRemoval = await removeOfflineCartItems();

    if (offlineRemoval.removed && variantId) {
      const updatedCart = await fetch("/cart.js").then((res) => res.json());
      const newLine = updatedCart.items.findIndex((item) => item.variant_id == variantId);
      if (newLine !== -1) {
        line = newLine + 1;
      }
    }

    const body = JSON.stringify({
      line,
      quantity,
      sections: this.getSectionsToRender().map((section) => section.section),
      sections_url: window.location.pathname,
    });

    await fetch(`${routes.cart_change_url}`, { ...fetchConfig(), ...{ body } })
      .then((response) => {
        return response.text();
      })
      .then(async (state) => {
        const parsedState = JSON.parse(state);
        const quantityElement =
          document.getElementById(`Quantity-${line}`) || document.getElementById(`Drawer-quantity-${line}`);
        const items = document.querySelectorAll(".cart-item");

        if (parsedState.errors) {
          quantityElement.value = quantityElement.getAttribute("value");
          this.updateLiveRegions(line, parsedState.errors);
          this.disableLoading(line);
          this.isUploading = false;
          return;
        }

        const swatches = document.querySelectorAll(".product-page .swatch-input__input, .product-dropdown-swatch");
        const quantityInput = document.querySelector('.product-page input[name="quantity"]');
        const plusBtn = document.querySelector(".product-page .product-form__actions .quantity__button[name='plus']");

        if (swatches && quantityInput && plusBtn) {
          swatches.forEach((swatch) => {
            const currentItem = parsedState.items.find(
              (item) => swatch.dataset.variantId == item.variant_id && swatch.dataset.productId == item.product_id,
            );

            const maxProductQuantity = swatch.dataset.maxQuantity;
            if (currentItem) {
              const quantity_in_cart = currentItem.quantity;
              swatch.setAttribute("data-quantity-in-cart", quantity_in_cart);
            } else {
              swatch.setAttribute("data-quantity-in-cart", 0);
            }

            if ((swatch.checked || swatch.classList.contains("selected")) && currentItem) {
              const quantity_in_cart = currentItem.quantity;

              quantityInput.setAttribute("data-cart-quantity", quantity_in_cart);
              if (parseInt(quantity_in_cart) !== parseInt(maxProductQuantity)) {
                quantityInput.setAttribute("max", maxProductQuantity);
                quantityInput.max = maxProductQuantity;
                plusBtn?.classList.remove("disabled");
              }
            }
          });
        }

        let workingState = parsedState;

        workingState = await this.removeGiftsIfIneligible(workingState);

        const cartDrawerWrapper = isCartDrawerContext ? document.querySelector("cart-drawer") : null;
        const cartFooter = document.getElementById("main-cart-footer");

        let sectionsToRender = workingState.sections;
        if (!sectionsToRender) {
          sectionsToRender = await this.fetchSectionsJson();
        }

        this.classList.toggle("is-empty", workingState.item_count === 0);
        if (cartFooter) cartFooter.classList.toggle("is-empty", workingState.item_count === 0);
        if (cartDrawerWrapper) cartDrawerWrapper.classList.toggle("is-empty", workingState.item_count === 0);

        if (workingState.item_count === 0) {
          sessionStorage.removeItem("dk_auto_added_gifts");
        }

        this.renderFromSections(sectionsToRender);

        if (preservedScrollPosition) {
          requestAnimationFrame(() => {
            window.scrollTo({
              left: preservedScrollPosition.left,
              top: preservedScrollPosition.top,
              behavior: "auto",
            });
          });
        }

        const updatedValue = workingState.items[line - 1] ? workingState.items[line - 1].quantity : undefined;
        let message = "";
        if (items.length === workingState.items.length && updatedValue !== parseInt(quantityElement.value)) {
          if (typeof updatedValue === "undefined") {
            message = window.cartStrings.error;
          } else {
            message = window.cartStrings.quantityError.replace("[quantity]", updatedValue);
          }
        }
        this.updateLiveRegions(line, message);

        const lineItem =
          document.getElementById(`CartItem-${line}`) || document.getElementById(`CartDrawer-Item-${line}`);
        const focusTarget = name ? lineItem?.querySelector(`[name="${name}"]`) : null;

        if (lineItem && focusTarget) {
          cartDrawerWrapper
            ? !shouldClearCartActionFocus && trapFocus(cartDrawerWrapper, focusTarget)
            : !shouldClearCartActionFocus && focusTarget.focus({ preventScroll: true });
        } else if (workingState.item_count === 0 && cartDrawerWrapper) {
          trapFocus(cartDrawerWrapper.querySelector(".drawer__inner-empty"), cartDrawerWrapper.querySelector("a"));
        } else if (document.querySelector(".cart-item") && cartDrawerWrapper) {
          trapFocus(cartDrawerWrapper, document.querySelector(".cart-item__name"));
        }

        publish(PUB_SUB_EVENTS.cartUpdate, {
          source: "cart-items",
          cartData: workingState,
          variantId,
        });
      })
      .catch((error) => {
        console.error("Cart request failed:", error);
        this.querySelectorAll(".loading__spinner").forEach((overlay) => overlay.classList.add("hidden"));
        const errors = document.getElementById("cart-errors") || document.getElementById("CartDrawer-CartErrors");
        errors.textContent = window.cartStrings.error;
      })
      .finally(async () => {
        await this.releaseOverlayAfterGifts();
      });
    });
    } finally {
      this.isUploading = false;
    }
  }

  updateLiveRegions(line, message) {
    const lineItemError =
      document.getElementById(`Line-item-error-${line}`) || document.getElementById(`CartDrawer-LineItemError-${line}`);
    if (lineItemError) {
      const errorTextElement = lineItemError.querySelector(".cart-item__error-text");

      if (message && message.trim() !== "") {
        errorTextElement.textContent = message;
        lineItemError.classList.remove("d-none");
      } else {
        errorTextElement.textContent = "";
        lineItemError.classList.add("d-none");
      }
    }

    this.lineItemStatusElement.setAttribute("aria-hidden", true);

    const cartStatus =
      document.getElementById("cart-live-region-text") || document.getElementById("CartDrawer-LiveRegionText");
    cartStatus.setAttribute("aria-hidden", false);

    setTimeout(() => {
      cartStatus.setAttribute("aria-hidden", true);
    }, 1000);
  }

  getSectionInnerHTML(html, selector) {
    return new DOMParser().parseFromString(html, "text/html").querySelector(selector).innerHTML;
  }

  toggleOverlay(isLoading) {
    const _cartDrawer = document?.getElementById("CartDrawer");
    const _mainCartItems = document.querySelector(".cart-page");
    const _cartPage = _mainCartItems?.closest("#MainContentContainer");

    if (_cartDrawer) {
      _cartDrawer.classList.toggle("cart-processing", isLoading);
    }

    if (_cartPage) {
      _cartPage.classList.toggle("cart-page-processing", isLoading);
      if (isLoading) {
          if (!this._cartSpinnerHandler) {
            this._cartSpinnerHandler = () => {
              const footer = document.querySelector("footer");
              if (!footer) {
                _cartPage.style.removeProperty("--cart-spinner-top");
                _cartPage.classList.remove("hide-spinner");
                return;
              }

              const footerTop = footer.getBoundingClientRect().top;
              const center = window.innerHeight / 2;
              const spinnerHalf = 20;
              const gap = 30;

              if (footerTop > center + spinnerHalf + gap) {
                _cartPage.style.removeProperty("--cart-spinner-top");
                _cartPage.classList.remove("hide-spinner");
                return;
              }

              const minTop = 60;
              const topPx = Math.max(minTop, footerTop - spinnerHalf - gap);

              if (footerTop - spinnerHalf - gap < minTop) {
                _cartPage.classList.add("hide-spinner");
                return;
              }

              _cartPage.classList.remove("hide-spinner");
              _cartPage.style.setProperty("--cart-spinner-top", `${topPx}px`);
            };
          }

          this._cartSpinnerHandler();
          if (!this._spinnerListening) {
            window.addEventListener("scroll", this._cartSpinnerHandler, { passive: true });
            window.addEventListener("resize", this._cartSpinnerHandler);
            this._spinnerListening = true;
          }
      } else {
        _cartPage.style.removeProperty("--cart-spinner-top");
        _cartPage.classList.remove("hide-spinner");

        if (this._spinnerListening && this._cartSpinnerHandler) {
          window.removeEventListener("scroll", this._cartSpinnerHandler);
          window.removeEventListener("resize", this._cartSpinnerHandler);
          this._spinnerListening = false;
        }
      }
    }
  }

  _giftWaitInFlight = null;
  async releaseOverlayAfterGifts() {
    try {
      this._giftWaitInFlight ||= (async () => {
        await waitForGiftCartIfBusy();
        return true;
      })();

      await this._giftWaitInFlight;
    } finally {
      this._giftWaitInFlight = null;
      this.toggleOverlay(false);
    }
  }

  enableLoading(line) {
    this.toggleOverlay(true);
    //TODO: comment out when need the loader

    // const cartItemElements = this.querySelectorAll(`#CartItem-${line} .loading__spinner`);
    // const cartDrawerItemElements = this.querySelectorAll(`#CartDrawer-Item-${line} .loading__spinner`);

    // [...cartItemElements, ...cartDrawerItemElements].forEach((overlay) => overlay.classList.remove("hidden"));

    // document.activeElement.blur();
    // this.lineItemStatusElement.setAttribute("aria-hidden", false);
  }

  disableLoading(line) {
    this.releaseOverlayAfterGifts();
    //TODO: comment out when need the loader
    // const cartItemElements = this.querySelectorAll(`#CartItem-${line} .loading__spinner`);
    // const cartDrawerItemElements = this.querySelectorAll(`#CartDrawer-Item-${line} .loading__spinner`);

    // cartItemElements.forEach((overlay) => overlay.classList.add("hidden"));
    // cartDrawerItemElements.forEach((overlay) => overlay.classList.add("hidden"));
  }
}

customElements.define("cart-items", CartItems);

const CART_PROMO_SELECTORS = Object.freeze({
  form: "[data-cart-promo-form]",
  message: "[data-cart-promo-message]",
  badges: "[data-cart-promo-badges]",
  badge: "[data-cart-promo-badge]",
  badgeCode: "[data-cart-promo-code]",
  input: "[data-cart-promo-input]",
  remove: "[data-cart-promo-remove]",
  submit: "[data-cart-promo-submit]",
});

const setCartPromoMessage = (form, message = "", tone = "info") => {
  const messageElement = form?.querySelector(CART_PROMO_SELECTORS.message);
  if (!messageElement) return;

  const normalizedMessage = String(message ?? "").trim();
  messageElement.textContent = normalizedMessage;
  const hasMessage = normalizedMessage !== "";

  messageElement.hidden = !hasMessage;

  if (hasMessage) {
    messageElement.dataset.state = tone;
    return;
  }

  messageElement.removeAttribute("data-state");
};

const getCartPromoForm = () => document.querySelector(CART_PROMO_SELECTORS.form);

const getCartPromoBadgesContainer = (form = getCartPromoForm()) => form?.querySelector(CART_PROMO_SELECTORS.badges);

const getCartPromoCartItems = () => document.querySelector("cart-items");

const startCartPromoPageLoading = () => {
  const cartItems = getCartPromoCartItems();
  if (!cartItems || typeof cartItems.toggleOverlay !== "function") return false;

  cartItems.toggleOverlay(true);
  return true;
};

const stopCartPromoPageLoading = async () => {
  const cartItems = getCartPromoCartItems();
  if (!cartItems) return;

  if (typeof cartItems.releaseOverlayAfterGifts === "function") {
    await cartItems.releaseOverlayAfterGifts();
    return;
  }

  if (typeof cartItems.toggleOverlay === "function") {
    cartItems.toggleOverlay(false);
  }
};

const getCartPromoDatasetMessage = (form, datasetKey, fallback) =>
  String(form?.dataset?.[datasetKey] ?? fallback ?? "").trim();

const getCartPromoErrorMessage = () =>
  window.cartStrings?.error || "There was an error while updating your cart. Please try again.";

const setCartPromoLoading = (form, isLoading, options = {}) => {
  const { showSubmitLoader = true } = options;
  const submitButton = form?.querySelector(CART_PROMO_SELECTORS.submit);
  const input = form?.querySelector(CART_PROMO_SELECTORS.input);
  const removeButtons = form?.querySelectorAll(CART_PROMO_SELECTORS.remove) || [];

  form?.classList.toggle("is-loading", isLoading);
  if (submitButton) {
    submitButton.disabled = isLoading;
    submitButton.classList.toggle("is-loading", isLoading && showSubmitLoader);
    submitButton.setAttribute("aria-busy", isLoading && showSubmitLoader ? "true" : "false");
  }

  if (input) {
    input.disabled = isLoading;
  }

  removeButtons.forEach((button) => {
    button.disabled = isLoading;
  });
};

const normalizeCartPromoCode = (code) => String(code ?? "").trim();

const escapeCartPromoHtml = (value) =>
  String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");

const dedupeCartPromoCodes = (codes = []) => {
  const uniqueCodes = [];
  const seen = new Set();

  codes.forEach((value) => {
    const code = normalizeCartPromoCode(value);
    if (!code) return;

    const key = code.toLowerCase();
    if (seen.has(key)) return;

    seen.add(key);
    uniqueCodes.push(code);
  });

  return uniqueCodes;
};

const hasActiveCartPromoAmount = (amount) => {
  const numericAmount = Number(amount);
  return !Number.isFinite(numericAmount) || numericAmount > 0;
};

const isCartPromoCodeLike = (code = "") => /^[a-z0-9][a-z0-9\-_.:%$]*$/i.test(code);

const getCartPromoApplicationCode = (application) => {
  if (!application) return "";

  const type = String(application.type || application.application_type || "").toLowerCase();
  const explicitCode = normalizeCartPromoCode(application.code);
  const title = normalizeCartPromoCode(application.title);
  if (explicitCode) return explicitCode;
  if (!title) return "";

  if (type === "discount_code") {
    return title;
  }

  if (isCartPromoCodeLike(title)) {
    return title;
  }

  return "";
};

const getCurrentCartPromoCodes = (cartState) => {
  const codes = [];
  const addCode = (value) => {
    const nextCodes = dedupeCartPromoCodes([...codes, value]);
    codes.length = 0;
    codes.push(...nextCodes);
  };

  (cartState?.cart_level_discount_applications || []).forEach((application) => {
    if (!hasActiveCartPromoAmount(application?.total_allocated_amount)) return;
    addCode(getCartPromoApplicationCode(application));
  });

  (cartState?.items || []).forEach((item) => {
    (item?.line_level_discount_allocations || []).forEach((allocation) => {
      if (!hasActiveCartPromoAmount(allocation?.amount)) return;
      addCode(getCartPromoApplicationCode(allocation?.discount_application));
    });

    (item?.discounts || []).forEach((discount) => {
      if (!hasActiveCartPromoAmount(discount?.amount ?? discount?.total_allocated_amount)) return;
      addCode(getCartPromoApplicationCode(discount));
    });
  });

  return codes;
};

const getCartPromoRemoveLabel = (form = getCartPromoForm()) =>
  normalizeCartPromoCode(form?.dataset.removeLabel) || "Remove";

const renderCartPromoBadges = (form, codes = []) => {
  const badgesContainer = getCartPromoBadgesContainer(form);
  if (!badgesContainer) return;

  const normalizedCodes = dedupeCartPromoCodes(codes);
  badgesContainer.hidden = normalizedCodes.length === 0;

  if (!normalizedCodes.length) {
    badgesContainer.innerHTML = "";
    return;
  }

  const removeLabel = getCartPromoRemoveLabel(form);

  badgesContainer.innerHTML = normalizedCodes
    .map((code) => {
      const escapedCode = escapeCartPromoHtml(code);
      const escapedLabel = escapeCartPromoHtml(`${removeLabel} ${code}`);

      return `
        <span class="cart-promo-code__badge" data-cart-promo-badge data-cart-promo-code="${escapedCode}" role="listitem">
          <span class="cart-promo-code__badge-text">${escapedCode}</span>
          <button class="cart-promo-code__badge-remove" type="button" data-cart-promo-remove="${escapedCode}" aria-label="${escapedLabel}">
            <span aria-hidden="true">&times;</span>
          </button>
        </span>
      `;
    })
    .join("");
};

const getCartPromoStateFingerprint = (cartState) => {
  const totalsFingerprint = [
    Number(cartState?.original_total_price ?? 0),
    Number(cartState?.items_subtotal_price ?? 0),
    Number(cartState?.total_price ?? 0),
    Number(cartState?.total_discount ?? 0),
  ].join(":");

  const cartLevelDiscountsFingerprint = (cartState?.cart_level_discount_applications || [])
    .map((application) => {
      const title = normalizeCartPromoCode(application?.title || application?.code);
      const amount = Number(application?.total_allocated_amount ?? 0);
      return `${title}:${amount}`;
    })
    .sort()
    .join("|");

  const lineItemsFingerprint = (cartState?.items || [])
    .map((item) => {
      const lineDiscountsFingerprint = (item?.line_level_discount_allocations || [])
        .map((allocation) => {
          const application = allocation?.discount_application || {};
          const title = normalizeCartPromoCode(application?.title || application?.code);
          const amount = Number(allocation?.amount ?? 0);
          return `${title}:${amount}`;
        })
        .sort()
        .join(",");

      return [
        String(item?.key || item?.id || ""),
        Number(item?.quantity ?? 0),
        Number(item?.original_line_price ?? 0),
        Number(item?.final_line_price ?? item?.line_price ?? 0),
        lineDiscountsFingerprint,
      ].join(":");
    })
    .join("|");

  return [totalsFingerprint, cartLevelDiscountsFingerprint, lineItemsFingerprint].join("::");
};

const getCartPromoCodesFromDOM = () => {
  const badgeCodes = Array.from(document.querySelectorAll(CART_PROMO_SELECTORS.badgeCode), (element) =>
    normalizeCartPromoCode(element.dataset.cartPromoCode),
  );

  if (badgeCodes.length) {
    return dedupeCartPromoCodes(badgeCodes);
  }

  const discountElements = document.querySelectorAll("#main-cart-footer .discounts__discount");

  return dedupeCartPromoCodes(
    Array.from(discountElements, (element) =>
      normalizeCartPromoCode(element.textContent.replace(/\s*\([^)]*\)\s*$/, "")),
    ),
  );
};

const fetchCurrentCartPromoState = async () => {
  const response = await fetch(`${routes.cart_url}.js`, {
    credentials: "same-origin",
  });

  if (!response.ok) {
    throw new Error("Failed to load current cart discounts");
  }

  const cartState = await response.json();
  return {
    fingerprint: getCartPromoStateFingerprint(cartState),
    codes: getCurrentCartPromoCodes(cartState),
  };
};

const getCartPromoSectionsToRender = () => {
  const cartItems = document.querySelector("cart-items");
  if (cartItems && typeof cartItems.getSectionsToRender === "function") {
    return cartItems.getSectionsToRender().filter((section) => section.section);
  }

  const fallbackSections = [
    {
      id: "main-cart-items",
      section: document.getElementById("main-cart-items")?.dataset.id,
      selector: ".js-contents",
    },
    {
      id: "cart-samples-gifts-tabs",
      section: document.getElementById("cart-samples-gifts-tabs")?.dataset.id,
      selector: ".js-contents",
    },
    {
      id: "cart-live-region-text",
      section: "cart-live-region-text",
      selector: ".shopify-section",
    },
    {
      id: "main-cart-footer",
      section: document.getElementById("main-cart-footer")?.dataset.id,
      selector: ".cart__footer",
    },
  ];

  return fallbackSections.filter((section) => section.section);
};

const getCartPromoSectionInnerHTML = (html, selector) =>
  new DOMParser().parseFromString(html, "text/html").querySelector(selector)?.innerHTML || "";

const renderCartPromoSections = (sectionsMap) => {
  getCartPromoSectionsToRender().forEach((section) => {
    const container = document.getElementById(section.id);
    if (!container) return;

    const elementToReplace = container.querySelector(section.selector) || container;
    const html = sectionsMap?.[section.section];
    if (!html) return;

    elementToReplace.innerHTML = getCartPromoSectionInnerHTML(html, section.selector);
  });
};

const refreshCartAfterPromoUpdate = async (updatedCart = null) => {
  const cartItems = document.querySelector("cart-items");
  if (cartItems && typeof cartItems.updateCartPage === "function") {
    await cartItems.updateCartPage();
    return;
  }

  if (updatedCart?.sections) {
    renderCartPromoSections(updatedCart.sections);
    return;
  }

  const sections = getCartPromoSectionsToRender()
    .map((section) => section.section)
    .join(",");

  if (!sections) return;

  const response = await fetch(`${routes.cart_url}?sections=${encodeURIComponent(sections)}`, {
    credentials: "same-origin",
  });

  if (!response.ok) {
    throw new Error("Failed to refresh cart sections");
  }

  const sectionsMap = await response.json();
  renderCartPromoSections(sectionsMap);
};

const getVerifiedCartPromoCodes = (updatedCart = null, refreshedPromoState = null) => {
  const refreshedCodes = dedupeCartPromoCodes(refreshedPromoState?.codes || []);
  if (refreshedCodes.length) {
    return refreshedCodes;
  }

  const updatedCartCodes = dedupeCartPromoCodes(getCurrentCartPromoCodes(updatedCart));
  if (updatedCartCodes.length) {
    return updatedCartCodes;
  }

  return dedupeCartPromoCodes(getCartPromoCodesFromDOM());
};

const updateCartPromoCodes = async (form, nextCodes, options = {}) => {
  let isPageLoading = false;
  const normalizedNextCodes = dedupeCartPromoCodes(nextCodes);
  const showSubmitLoader = options.showSubmitLoader !== false;
  const manageFormLoading = options.manageFormLoading !== false;
  const pageLoadingStarted = options.pageLoadingStarted === true;

  setCartPromoMessage(form);
  if (manageFormLoading) {
    setCartPromoLoading(form, true, { showSubmitLoader });
  }

  try {
    const payload = {
      discount: normalizedNextCodes.join(","),
      sections: getCartPromoSectionsToRender().map((section) => section.section),
      sections_url: window.location.pathname,
    };

    if (pageLoadingStarted) {
      isPageLoading = true;
    } else {
      isPageLoading = startCartPromoPageLoading();
    }

    const response = await fetch(`${routes.cart_update_url}`, {
      ...fetchConfig(),
      body: JSON.stringify(payload),
    });

    const updatedCart = await response.json();

    if (!response.ok) {
      throw new Error(updatedCart?.description || updatedCart?.message || "Cart update failed");
    }

    const giftCheckSections = getCartPromoSectionsToRender().map((s) => s.section).filter(Boolean);
    const cleanedCart = await removeIneligibleGiftsWithSingleRequest(updatedCart, giftCheckSections);

    await refreshCartAfterPromoUpdate(cleanedCart);

    let refreshedPromoState = null;
    try {
      refreshedPromoState = await fetchCurrentCartPromoState();
    } catch (refreshError) {
      console.error("Promo code cart refresh verification failed:", refreshError);
    }

    const verifiedCodes = getVerifiedCartPromoCodes(cleanedCart, refreshedPromoState);
    const activeForm = getCartPromoForm() || form;
    const submittedCode = normalizeCartPromoCode(options.submittedCode);

    if (submittedCode) {
      const submittedCodeApplied = verifiedCodes.some(
        (existingCode) => existingCode.toLowerCase() === submittedCode.toLowerCase(),
      );
      const beforeFingerprint = options.beforeFingerprint || "";
      const afterFingerprint = refreshedPromoState?.fingerprint || getCartPromoStateFingerprint(cleanedCart);
      const shouldShowInvalidPromoMessage = !submittedCodeApplied && beforeFingerprint === afterFingerprint;

      if (shouldShowInvalidPromoMessage) {
        const fallbackCodes = dedupeCartPromoCodes(options.existingCodes || []);
        renderCartPromoBadges(activeForm, fallbackCodes);
        setCartPromoMessage(
          activeForm,
          getCartPromoDatasetMessage(form, "invalidMessage", "This promo code isn't valid for your cart."),
          "error",
        );

        return {
          codes: fallbackCodes,
          invalid: true,
        };
      }
    }

    renderCartPromoBadges(activeForm, verifiedCodes);

    return {
      codes: verifiedCodes,
      invalid: false,
    };
  } catch (error) {
    console.error("Promo code update failed:", error);
    setCartPromoMessage(form, getCartPromoErrorMessage(), "error");

    return {
      codes: dedupeCartPromoCodes(options.existingCodes || []),
      invalid: false,
    };
  } finally {
    setCartPromoLoading(form, false);
    if (isPageLoading) {
      await stopCartPromoPageLoading();
    }
  }
};

const syncCartPromoBadgesFromCart = async () => {
  const form = getCartPromoForm();
  if (!form) return;

  const initialDomCodes = getCartPromoCodesFromDOM();
  renderCartPromoBadges(form, initialDomCodes);

  try {
    const cartPromoState = await fetchCurrentCartPromoState();
    const existingCodes = dedupeCartPromoCodes(cartPromoState.codes.length ? cartPromoState.codes : initialDomCodes);
    renderCartPromoBadges(form, existingCodes);
  } catch (error) {
    renderCartPromoBadges(form, initialDomCodes);
  }
};

const initCartPromoCodeForm = () => {
  if (window.__hbCartPromoCodeFormInitialized) return;
  window.__hbCartPromoCodeFormInitialized = true;

  document.addEventListener("submit", async (event) => {
    const form = event.target.closest(CART_PROMO_SELECTORS.form);
    if (!form) return;

    event.preventDefault();

    const input = form.querySelector(CART_PROMO_SELECTORS.input);
    const code = normalizeCartPromoCode(input?.value);

    if (!code) {
      setCartPromoMessage(form, getCartPromoDatasetMessage(form, "emptyMessage", "Please enter a promo code."), "error");
      input?.focus();
      return;
    }

    let promoUpdateStarted = false;
    setCartPromoLoading(form, true, { showSubmitLoader: true });

    try {
      const cartPromoState = await fetchCurrentCartPromoState();
      const existingCodes = dedupeCartPromoCodes([
        ...getCartPromoCodesFromDOM(),
        ...cartPromoState.codes,
      ]);
      renderCartPromoBadges(form, existingCodes);

      if (existingCodes.some((existingCode) => existingCode.toLowerCase() === code.toLowerCase())) {
        setCartPromoMessage(
          form,
          getCartPromoDatasetMessage(form, "alreadyAppliedMessage", "This promo code is already applied."),
          "info",
        );
        return;
      }

      const mergedCodes = [
        ...existingCodes.filter((existingCode) => existingCode.toLowerCase() !== code.toLowerCase()),
        code,
      ];

      promoUpdateStarted = true;
      await updateCartPromoCodes(form, mergedCodes, {
        beforeFingerprint: cartPromoState.fingerprint,
        existingCodes,
        manageFormLoading: false,
        showSubmitLoader: true,
        submittedCode: code,
      });
    } catch (error) {
      setCartPromoMessage(form, getCartPromoErrorMessage(), "error");
    } finally {
      if (!promoUpdateStarted) {
        setCartPromoLoading(form, false);
      }
    }
  });

  document.addEventListener("click", async (event) => {
    const removeButton = event.target.closest(CART_PROMO_SELECTORS.remove);
    if (!removeButton) return;

    const form = removeButton.closest(CART_PROMO_SELECTORS.form);
    if (!form) return;

    const code = normalizeCartPromoCode(removeButton.dataset.cartPromoRemove);
    if (!code) return;

    const badge = removeButton.closest(CART_PROMO_SELECTORS.badge);
    let promoUpdateStarted = false;
    let pageLoadingStarted = false;
    badge?.classList.add("is-removing");
    setCartPromoLoading(form, true, { showSubmitLoader: false });
    pageLoadingStarted = startCartPromoPageLoading();

    try {
      const cartPromoState = await fetchCurrentCartPromoState();
      const existingCodes = dedupeCartPromoCodes([
        ...getCartPromoCodesFromDOM(),
        ...cartPromoState.codes,
      ]);
      const remainingCodes = existingCodes.filter((existingCode) => existingCode.toLowerCase() !== code.toLowerCase());

      if (remainingCodes.length === existingCodes.length) {
        renderCartPromoBadges(form, existingCodes);
        return;
      }

      promoUpdateStarted = true;
      await updateCartPromoCodes(form, remainingCodes, {
        beforeFingerprint: cartPromoState.fingerprint,
        existingCodes,
        manageFormLoading: false,
        pageLoadingStarted: pageLoadingStarted,
        showSubmitLoader: false,
      });
    } catch (error) {
      console.error("Promo code removal setup failed:", error);
      setCartPromoMessage(form, getCartPromoErrorMessage(), "error");
    } finally {
      badge?.classList.remove("is-removing");
      if (!promoUpdateStarted) {
        setCartPromoLoading(form, false);
        if (pageLoadingStarted) {
          await stopCartPromoPageLoading();
        }
      }
    }
  });

  document.addEventListener("input", (event) => {
    const input = event.target.closest(CART_PROMO_SELECTORS.input);
    if (!input) return;

    setCartPromoMessage(input.closest(CART_PROMO_SELECTORS.form));
  });

  syncCartPromoBadgesFromCart();
};

initCartPromoCodeForm();

if (!customElements.get("cart-note")) {
  customElements.define(
    "cart-note",
    class CartNote extends HTMLElement {
      constructor() {
        super();

        this.addEventListener(
          "input",
          debounce((event) => {
            const body = JSON.stringify({ note: event.target.value });
            fetch(`${routes.cart_update_url}`, { ...fetchConfig(), ...{ body } });
          }, ON_CHANGE_DEBOUNCE_TIMER),
        );
      }
    },
  );
}

const setHazmatDismissed = (value) => {
  if (value) {
    localStorage.setItem("hazmatMessageDismissed", "true");
  } else {
    localStorage.removeItem("hazmatMessageDismissed");
  }
};

const isHazmatDismissed = () => {
  return localStorage.getItem("hazmatMessageDismissed") === "true";
};

document.addEventListener("click", function (e) {
  if (e.target.classList.contains("ky-close-btn")) {
    e.target.parentElement.style.display = "none";
    setHazmatDismissed(true);
  }
});

const showMessagesIfNeeded = () => {
  if (isHazmatDismissed()) return;
};

const observer = new MutationObserver(() => {
  showMessagesIfNeeded();
});

observer.observe(document.body, {
  childList: true,
  subtree: true,
});
